import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, concatMap, debounceTime, filter, iif, map, merge, of, switchMap, take, tap } from 'rxjs';

import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';

import {
  combineReasons,
  PrescriptionsService,
  addPrescriptionDraft,
  updatePrescriptionDraft,
  reasonNotValid,
  APPROVE_REASON,
  DECLINE_REASONS,
  EDIT_REASONS,
  GuaranteeStatus,
  FranchisePrescriptionType,
  GenericServiceControls,
  Prescription,
  PrescriptionControls,
  PrescriptionItem,
  PrescriptionTypes,
  ServiceItemMetadata,
} from '@lib/prescriptions/data-access';
import { ServiceDictionary } from '@lib/core/models/protocol.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

const DEFAULT_MIN_PERIOD: number = 14;
const LABORATORY_MIN_PERIOD: number = 14;
const DIAGNOSTICS_MIN_PERIOD: number = 14;
const SPECIALISTS_MIN_PERIOD: number = 14;

@Component({
  selector: 'prescriptions-service-prescription-form-modal',
  standalone: true,
  imports: [
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormFieldModule,
    MatInput,
    MatOption,
    ReactiveFormsModule,
    MatSelect,
    MatDatepickerModule,
    LoaderComponent,
    MatCheckboxModule,
    MatIcon,
    MatIconButton,
    MatTooltip,
    TranslatePipe,
    NgClass,
  ],
  templateUrl: './service-prescription-form-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicePrescriptionFormModalComponent implements OnInit {
  private readonly data: {
    prescription: Prescription | null;
    prescriptionType: PrescriptionTypes;
    memberId: string;
    guaranteeApplicationId: string;
    pharmaciesCount: number;
  } = inject(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<ServicePrescriptionFormModalComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly prescriptionsService = inject(PrescriptionsService);
  private readonly store = inject(Store);

  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly guaranteeStatus = GuaranteeStatus;
  protected readonly declineReasons: string[] = DECLINE_REASONS;
  protected readonly editReasons: string[] = EDIT_REASONS;
  protected readonly guaranteeStatusOptions: GuaranteeStatus[] = [this.guaranteeStatus.Guaranteed, this.guaranteeStatus.NotGuaranteed, this.guaranteeStatus.Auto];

  prescriptionType = signal<PrescriptionTypes>(this.data.prescriptionType);
  service = signal<Prescription>(this.data.prescription);
  memberId = signal<string>(this.data.memberId);
  guaranteeApplicationId = signal<string>(this.data.guaranteeApplicationId);

  protected readonly loading = signal<boolean>(true);
  protected readonly statusChanged = signal<boolean>(false);
  protected readonly dataChanged = signal<boolean>(false);
  protected readonly completionChecked = signal<boolean>(false);
  protected readonly servicesOptions = signal<ServiceDictionary[]>([]);
  protected readonly filteredDeclineReasons = signal<string[]>(this.declineReasons);
  protected readonly filteredEditReasons = signal<string[]>(this.editReasons);
  protected readonly franchiseInitialValue = signal<number>(0);
  protected readonly originalMappingInvalid = signal<boolean>(false);
  protected readonly mappingInvalid = signal<boolean>(false);

  protected readonly editableServiceTypes: PrescriptionTypes[] = [
    PrescriptionTypes.Appointment,
    PrescriptionTypes.LaboratoryService,
    PrescriptionTypes.InstrumentalDiagnostic,
  ];

  protected serviceForm: FormGroup<PrescriptionControls<GenericServiceControls>>;
  protected startDate = new Date();
  protected endDate = new Date(this.startDate);

  protected get fc(): PrescriptionControls<GenericServiceControls> {
    return this.serviceForm?.controls;
  }

  protected get metadataFc(): GenericServiceControls {
    return this.serviceForm.controls.metadata.controls;
  }

  ngOnInit() {
    this.initServiceForm();
  }

  private initServiceForm() {
    this.serviceForm = this.fb.group<PrescriptionControls<GenericServiceControls>>({
      type: this.fb.control<PrescriptionTypes | null>(this.prescriptionType(), [Validators.required]),
      name: this.fb.control<string | null>(null, [Validators.required]),
      startDate: this.fb.control<Date | null>(this.startDate, [Validators.required]),
      endDate: this.fb.control<Date | null>(this.getEndDate(1, this.prescriptionType()), [Validators.required]),
      metadata: this.fb.group<GenericServiceControls>({
        quantity: this.fb.control<number | null>(1, [Validators.required, Validators.min(1)]),
        code: this.fb.control<string | null>(null, [Validators.required]),
      }),
      guaranteeStatus: this.fb.control<string | null>(null, [Validators.required]),
      editReason: this.fb.control<string | null>(null),
      statusReason: this.fb.control<string>(null),
      sendGuaranteeNotification: this.fb.control<boolean | null>(true),
      franchise: this.fb.control<number>(0, [Validators.required, Validators.min(0), Validators.max(100)]),
      franchiseManualChangeConfirmation: this.fb.control<boolean>(true, [Validators.requiredTrue]),
      completedInAppointment: this.fb.control<boolean>(false),
      originalPrescriptionId: this.fb.control<string | null>(null),
    });
    this.watchAllDataChanges();
    this.watchStatusChanges();
    this.watchCompletionCheckboxChanges();
    this.watchDeclineReasonsChanges();
    this.watchEditReasonsChanges();
    this.watchFranchiseChanges();
    this.watchMappingValidity();
    if (this.service()) {
      this.patchServiceForm(this.service());
      const isInvalid = !(this.service().metadata as ServiceItemMetadata)?.code;
      this.originalMappingInvalid.set(isInvalid);
      this.mappingInvalid.set(isInvalid);
    } else {
      this.statusChanged.set(true);
      this.fc.statusReason.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
      this.fc.statusReason.updateValueAndValidity({ emitEvent: false });
    }
    this.loading.set(false);
    this.fetchFranchise();
  }

  private patchServiceForm(service: Prescription) {
    this.serviceForm.patchValue(
      {
        type: service.type,
        name: service.name,
        startDate: new Date(service.startDate),
        endDate: new Date(service.endDate),
        metadata: {
          quantity: +(service.metadata as ServiceItemMetadata).quantity,
          code: (service.metadata as ServiceItemMetadata).code,
        },
        guaranteeStatus: service.guaranteeStatus as string,
        franchise: service.franchise || 0,
        originalPrescriptionId: service.originalPrescriptionId,
      },
      { emitEvent: false }
    );
    this.franchiseInitialValue.set(service.franchise || 0);
    if (this.fc.guaranteeStatus.value === this.guaranteeStatus.Auto || this.fc.guaranteeStatus.value === this.guaranteeStatus.Guaranteed) {
      this.fc.guaranteeStatus.disable();
    }
  }

  private fetchFranchise() {
    if (this.service()) {
      return;
    }
    this.loading.set(true);
    const serviceType = this.fc.type.value === PrescriptionTypes.LaboratoryService ? this.fc.type.value : FranchisePrescriptionType.Achi;
    this.prescriptionsService
      .fetchPrescriptionFranchiseByMemberId(this.memberId(), {
        requestDate: this.fc.startDate.value,
        dictionaryType: serviceType,
      })
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.franchise) {
            this.franchiseInitialValue.set(response.franchise);
            this.fc.franchise.setValue(response.franchise, { emitEvent: false });
          }
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected selectServiceOption(serviceFormGroup: AbstractControl, item: ServiceDictionary) {
    serviceFormGroup.patchValue(
      {
        name: item.nameUa,
        metadata: {
          code: item.code,
        },
      },
      { emitEvent: false }
    );
    if (this.service()) {
      this.mappingInvalid.set(!item.code);
    }
  }

  private watchAllDataChanges() {
    merge(
      this.watchServiceTypeChanges(),
      this.watchServicesSearch(),
      this.watchStartDateChanges(),
      this.watchEndDateChanges(),
      this.watchQuantityChanges(),
      this.watchFranchiseChanges()
    )
      .pipe(
        tap(() => {
          if (this.service()) {
            this.fc.editReason.setValidators([Validators.required]);
            this.fc.editReason.updateValueAndValidity();
            this.dataChanged.set(true);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchServiceTypeChanges() {
    return this.fc.type.valueChanges.pipe(
      tap(() => {
        this.servicesOptions.set([]);
        this.fillDateEndControl();
        if (this.originalMappingInvalid()) {
          this.fc.name.setValue(null, { emitEvent: false });
          this.metadataFc.code.setValue(null);
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchMappingValidity() {
    return this.metadataFc.code.valueChanges
      .pipe(
        tap(code => {
          if (this.service()) {
            this.mappingInvalid.set(!code);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchServicesSearch() {
    return this.fc.name.valueChanges.pipe(
      debounceTime(500),
      filter(searchString => !!searchString && searchString.length >= 3),
      switchMap(searchString => {
        return iif(
          () => this.fc.type.value === this.prescriptionTypes.LaboratoryService,
          this.prescriptionsService.searchLaboratoryService(searchString),
          this.prescriptionsService.searchAchiService(searchString)
        ).pipe(
          catchError(() => {
            return of({ value: [] as ServiceDictionary[] });
          })
        );
      }),
      tap(result => this.servicesOptions.set(result.value)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchStartDateChanges() {
    return this.fc.startDate.valueChanges.pipe(
      tap(() => this.fillDateEndControl()),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchEndDateChanges() {
    return this.fc.endDate.valueChanges.pipe(
      concatMap((value: Date) => {
        if (value) {
          value.setHours(23, 59, 59, 999);
        }
        return of(value);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchQuantityChanges() {
    return this.metadataFc.quantity.valueChanges.pipe(
      tap(() => this.fillDateEndControl()),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchFranchiseChanges() {
    return this.fc.franchise.valueChanges.pipe(
      tap(value => this.fc.franchiseManualChangeConfirmation.setValue(value === this.franchiseInitialValue())),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private watchStatusChanges() {
    this.fc.guaranteeStatus.valueChanges
      .pipe(
        tap((value: string) => {
          const statusReasonControl = this.fc.statusReason;
          if (value === this.guaranteeStatus.Guaranteed) {
            statusReasonControl.setValue(APPROVE_REASON);
            statusReasonControl.setValidators([Validators.required]);
          } else {
            statusReasonControl.setValue(null);
            statusReasonControl.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
          }
          if (this.service()) {
            const status = this.service().guaranteeStatus ? this.service().guaranteeStatus : this.service().status;
            if (value !== status) {
              this.statusChanged.set(true);
            } else {
              this.statusChanged.set(false);
              statusReasonControl.clearValidators();
            }
          }
          statusReasonControl.updateValueAndValidity();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchDeclineReasonsChanges() {
    this.fc.statusReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap((value: string) => {
          this.filteredDeclineReasons.set(this.declineReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchEditReasonsChanges() {
    this.fc.editReason.valueChanges
      .pipe(
        map(value => (value ? value : '')),
        tap((value: string) => {
          this.filteredEditReasons.set(this.editReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchCompletionCheckboxChanges() {
    return this.fc.completedInAppointment.valueChanges
      .pipe(
        tap(value => {
          this.completionChecked.set(!!value);
          if (value) {
            this.serviceForm.addControl('completionReason', this.fb.control(null, [Validators.required]));
          } else {
            this.serviceForm.removeControl('completionReason');
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private saveDraftPrescription() {
    if (this.service()) {
      return this.updateDraftPrescription();
    } else {
      return this.createDraftPrescription();
    }
  }

  private createDraftPrescription() {
    const formValue = {
      ...this.serviceForm.getRawValue(),
      id: crypto.randomUUID(),
      guaranteeId: this.guaranteeApplicationId() || null,
    };

    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(addPrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private updateDraftPrescription() {
    const formValue = { ...this.serviceForm.getRawValue(), id: this.service().id, guaranteeId: this.guaranteeApplicationId() || null };

    const combinedReason = combineReasons({
      editReason: formValue.editReason,
      statusReason: formValue.statusReason,
    });

    this.store.dispatch(updatePrescriptionDraft({ draft: { ...formValue, reason: combinedReason } }));
  }

  private savePrescriptionForm() {
    return this.service() ? this.updatePrescription() : this.createPrescription();
  }

  private createPrescription() {
    const formValue = this.serviceForm.getRawValue();

    const editReason = this.completionChecked() ? this.serviceForm.getRawValue().completionReason : '';

    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.createPrescription(this.memberId(), this.guaranteeApplicationId(), this.adaptFormValueToRequestBody(formValue, combinedReason));
  }

  private updatePrescription() {
    const formValue = this.serviceForm.getRawValue();

    let editReason: string;
    if (this.completionChecked() && !formValue.editReason) {
      editReason = formValue.completionReason;
    } else {
      editReason = formValue.editReason;
    }

    const combinedReason = combineReasons({
      editReason: editReason,
      statusReason: formValue.statusReason,
    });

    return this.prescriptionsService.updatePrescription(
      this.memberId(),
      this.guaranteeApplicationId(),
      this.service().id,
      this.adaptFormValueToRequestBody(formValue, combinedReason, this.service().id)
    );
  }

  private adaptFormValueToRequestBody(formValue: typeof this.serviceForm.value, combinedReason: string, id?: string) {
    const updatedDateTime = new Date(formValue.endDate);
    updatedDateTime.setHours(23, 59, 59, 999);
    return <PrescriptionItem<ServiceItemMetadata>>{
      id: id ? id : crypto.randomUUID(),
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: updatedDateTime,
      guaranteeStatus: formValue.guaranteeStatus,
      reason: combinedReason,
      metadata: {
        code: formValue.metadata.code,
        quantity: formValue.metadata.quantity.toString(),
      },
      sendGuaranteeNotification: formValue.sendGuaranteeNotification,
      franchise: formValue.franchise,
      completedInAppointment: formValue.completedInAppointment,
      completionReason: formValue.completedInAppointment ? formValue.completionReason : null,
      originalPrescriptionId: formValue.originalPrescriptionId,
    };
  }

  private getEndDate(prescriptionQuantity: number = 1, type?: string): Date {
    let daysAdded: number;
    switch (type) {
      case this.prescriptionTypes.LaboratoryService:
        daysAdded = LABORATORY_MIN_PERIOD * prescriptionQuantity;
        break;
      case this.prescriptionTypes.InstrumentalDiagnostic:
        daysAdded = DIAGNOSTICS_MIN_PERIOD * prescriptionQuantity;
        break;
      case this.prescriptionTypes.RelatedSpecialists:
        daysAdded = SPECIALISTS_MIN_PERIOD * prescriptionQuantity;
        break;
      default:
        daysAdded = DEFAULT_MIN_PERIOD * prescriptionQuantity;
    }
    const validStartDate = this.fc ? this.fc.startDate.value : this.startDate;
    this.endDate = new Date(validStartDate); // Reset date to start date
    this.endDate.setDate(this.endDate.getDate() + daysAdded); // Add days
    this.endDate.setHours(23, 59, 59, 999); // Set time to end of the day
    return this.endDate;
  }

  private fillDateEndControl(): void {
    const courseQuantity: number = this.metadataFc.quantity.value || 1;
    const serviceType: string = this.fc.type.value || '';
    this.fc.endDate.patchValue(this.getEndDate(courseQuantity, serviceType), { emitEvent: false });
  }

  protected clearStatusReason() {
    this.fc.statusReason.setValue(null);
  }

  protected clearEditReason() {
    this.fc.editReason.setValue(null);
  }

  protected submitPrescription(): void {
    this.serviceForm.markAllAsTouched();
    this.serviceForm.updateValueAndValidity();

    if (this.serviceForm.valid && (this.statusChanged() || this.dataChanged() || this.completionChecked())) {
      this.loading.set(true);
      this.savePrescriptionForm()
        .pipe(take(1))
        .subscribe({
          next: result => this.dialogRef.close(result),
          error: error => {
            console.error('Error:', error);
            this.loading.set(false);
          },
        });
    }
  }

  protected submitDraft(): void {
    this.serviceForm.markAllAsTouched();
    this.serviceForm.updateValueAndValidity();

    if (this.serviceForm.valid && (this.statusChanged() || this.dataChanged())) {
      this.loading.set(true);
      this.saveDraftPrescription();
      this.closeDialog();
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }
}
